import { accountIdFormat } from "@redotech/redo-account-id";
import { FulfillmentOrderAddressSchema } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { PrinterOptionsSchema } from "@redotech/redo-fulfillment-group-common/printer";
import {
  inventoryItemIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import {
  consolidationCartonIdFormat,
  consolidationIdFormat,
} from "@redotech/redo-warehouse-id";
import * as z from "zod";

const consolidationIdSchema = consolidationIdFormat.schema;
const accountIdSchema = accountIdFormat.schema;
const merchantAccountIdSchema = accountIdSchema;
const cartonIdSchema = consolidationCartonIdFormat.schema;
const locationIdSchema = locationIdFormat.schema;

export const consolidationCartonLabelSizes = ["4x2", "4x6"] as const;
export type ConsolidationCartonLabelSize =
  (typeof consolidationCartonLabelSizes)[number];
export const defaultConsolidationCartonLabelSize: ConsolidationCartonLabelSize =
  "4x6";

export const packingAllocationSchema = z.object({
  cartonId: cartonIdSchema,
  quantity: z.number().int().positive(),
});

export const packingUnitSchema = z.object({
  allocations: z.array(packingAllocationSchema),
  id: z.string().min(1),
  imageUrl: z.string().nullable(),
  sku: z.string().nullable(),
  subtitle: z.string().nullable(),
  title: z.string(),
});

export const packingCartonSchema = z.object({
  cartonNumber: z.number().int().positive(),
  id: cartonIdSchema,
  label: z.string(),
  packedQuantity: z.number().int().nonnegative(),
  status: z.enum(["open", "sealed"]),
});

export const packingProgressSchema = z.object({
  packedQuantity: z.number().int().nonnegative(),
});

export const packingPlanSchema = z.object({ type: z.literal("open") });

export const packingWorkSchema = z.object({
  availability: z.object({
    canCreateCarton: z.boolean(),
    canFinish: z.boolean(),
    canPack: z.boolean(),
    finishBlockedReason: z.string().nullable(),
  }),
  cartons: z.array(packingCartonSchema),
  packingPlan: packingPlanSchema,
  progress: packingProgressSchema,
  subject: z.object({
    id: z.string().min(1),
    subtitle: z.string().nullable(),
    title: z.string(),
  }),
  units: z.array(packingUnitSchema),
});

export const consolidationPackingOverviewSchema = z.object({
  canSeal: z.boolean(),
  merchants: z.array(
    z.object({
      accountId: merchantAccountIdSchema,
      name: z.string(),
      cartonCount: z.number().int().nonnegative(),
      packedQuantity: z.number().int().nonnegative(),
    }),
  ),
  subject: z.object({
    id: consolidationIdSchema,
    subtitle: z.string(),
    title: z.string(),
  }),
  unpackedQuantity: z.number().int().nonnegative(),
});

export const consolidationCartonSchema = z.object({
  accountId: accountIdSchema,
  cartonNumber: z.number().int().positive(),
  dimensionUnit: z.string().nullable(),
  height: z.number().positive().nullable(),
  id: cartonIdSchema,
  length: z.number().positive().nullable(),
  ownerAccountId: accountIdSchema.nullable(),
  ownerName: z.string().nullable(),
  packedQuantity: z.number().int().nonnegative(),
  parentCartonId: cartonIdSchema.nullable(),
  status: z.enum(["open", "sealed"]),
  weight: z.number().positive().nullable(),
  weightUnit: z.string().nullable(),
  width: z.number().positive().nullable(),
});

export const consolidationCartonContentSchema = z.object({
  id: z.string().min(1),
  imageUrl: z.string().nullable(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  quantity: z.number().int().positive(),
  sku: z.string().nullable(),
  subtitle: z.string().nullable(),
  title: z.string(),
});

const merchantPackingSessionInputSchema = z.object({
  consolidationId: consolidationIdSchema,
  merchantAccountId: merchantAccountIdSchema,
});

const packingWorkOutputSchema = z.object({ work: packingWorkSchema });

export const getConsolidationMerchantPackingSession = {
  name: "getConsolidationMerchantPackingSession" as const,
  input: merchantPackingSessionInputSchema,
  output: packingWorkOutputSchema,
};

export const getConsolidationPackingSummary = {
  name: "getConsolidationPackingSummary" as const,
  input: z.object({ consolidationId: consolidationIdSchema }),
  output: z.object({ overview: consolidationPackingOverviewSchema }),
};

export const getConsolidationPackingCartonLabel = {
  name: "getConsolidationPackingCartonLabel" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
    labelSize: z.enum(consolidationCartonLabelSizes).optional(),
    printerId: z.number().optional(),
    printerOptions: PrinterOptionsSchema.optional(),
  }),
  output: z.object({ url: z.string() }),
};

export const consolidationCartonFinalMileRouteSchema = z.object({
  destination: FulfillmentOrderAddressSchema.nullable(),
  origin: FulfillmentOrderAddressSchema.nullable(),
  originLocationId: locationIdSchema.nullable(),
  originOptions: z.array(
    z.object({
      address: FulfillmentOrderAddressSchema,
      id: locationIdSchema,
      isReturnsCenter: z.boolean(),
      name: z.string(),
    }),
  ),
  unavailableReason: z.string().nullable(),
});
export type ConsolidationCartonFinalMileRoute = z.infer<
  typeof consolidationCartonFinalMileRouteSchema
>;

export const getConsolidationCarton = {
  name: "getConsolidationCarton" as const,
  input: z.object({
    cartonId: cartonIdSchema,
    consolidationId: consolidationIdSchema,
  }),
  output: z.object({
    carton: consolidationCartonSchema.extend({
      contents: z.array(consolidationCartonContentSchema),
      finalMileRoute: consolidationCartonFinalMileRouteSchema,
    }),
  }),
};

export const getConsolidationCartons = {
  name: "getConsolidationCartons" as const,
  input: z.object({
    consolidationId: consolidationIdSchema,
    pageNumber: z.number().int().min(0).default(0),
    pageSize: z.number().int().min(1).max(100).default(25),
  }),
  output: z.object({ cartons: z.array(consolidationCartonSchema) }),
};

export const sealConsolidationPacking = {
  name: "sealConsolidationPacking" as const,
  input: z.object({ consolidationId: consolidationIdSchema }),
  output: z.object({}),
};

export const createConsolidationPackingCarton = {
  name: "createConsolidationPackingCarton" as const,
  input: merchantPackingSessionInputSchema,
  output: packingWorkOutputSchema.extend({ cartonId: cartonIdSchema }),
};

export const packConsolidationInventoryItem = {
  name: "packConsolidationInventoryItem" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
    inventoryItemId: inventoryItemIdFormat.schema,
  }),
  output: z.discriminatedUnion("status", [
    packingWorkOutputSchema.extend({
      cartonId: cartonIdSchema,
      status: z.literal("packed"),
      unitId: z.string().min(1),
    }),
    packingWorkOutputSchema.extend({
      cartonIds: z.array(cartonIdSchema).min(1),
      status: z.literal("already_packed"),
      unitId: z.string().min(1),
    }),
  ]),
};

export const ConsolidationCartonPutawayErrorCode = {
  CartonNotFound: "FULFILLMENT_CONSOLIDATION_CARTON_NOT_FOUND",
  CartonNotOpen: "FULFILLMENT_CONSOLIDATION_CARTON_NOT_OPEN",
  ConsolidationNotPackable: "FULFILLMENT_CONSOLIDATION_NOT_PACKABLE",
  InventoryItemAlreadyPacked:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_ALREADY_PACKED",
  InventoryItemNotEligible:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_NOT_ELIGIBLE",
  InventoryItemSplitBetweenBins:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_SPLIT_BETWEEN_BINS",
  InventoryItemWrongMerchant:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_WRONG_MERCHANT",
} as const;

export const putAwayInventoryItemIntoConsolidationCarton = {
  name: "putAwayInventoryItemIntoConsolidationCarton" as const,
  input: z.object({
    cartonId: cartonIdSchema,
    inventoryItemId: inventoryItemIdFormat.schema,
  }),
  output: z.object({}),
};

export const removeConsolidationPackingCarton = {
  name: "removeConsolidationPackingCarton" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
  }),
  output: packingWorkOutputSchema,
};

export const moveConsolidationPackingUnit = {
  name: "moveConsolidationPackingUnit" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    fromCartonId: cartonIdSchema,
    toCartonId: cartonIdSchema,
    unitId: z.string().min(1),
  }),
  output: packingWorkOutputSchema,
};

export const unpackConsolidationPackingUnit = {
  name: "unpackConsolidationPackingUnit" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
    unitId: z.string().min(1),
  }),
  output: packingWorkOutputSchema,
};

export type ConsolidationPackingOverview = z.infer<
  typeof consolidationPackingOverviewSchema
>;
export type ConsolidationCarton = z.infer<typeof consolidationCartonSchema>;
export type ConsolidationCartonContent = z.infer<
  typeof consolidationCartonContentSchema
>;
export type PackingCarton = z.infer<typeof packingCartonSchema>;
export type PackingUnit = z.infer<typeof packingUnitSchema>;
export type PackingWork = z.infer<typeof packingWorkSchema>;
